import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../cn';
import { DataTableShell, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../data-table';
import { balanceAfterNet, formatUsdAmount, formatUsdCurrency, ledgerDisplayStatus, signedNetAmount, usdLedgerCreatedAtCompact, usdLedgerPaymentMethodLabel, usdLedgerSourceLabel, usdLedgerStatusBadgeClass, usdLedgerStatusLabel, usdLedgerTypeLabel, usdPartyDisplayLabel, } from './ledgerUtils';
import { CopyTextButton } from '../portal-ledger/CopyTextButton';
// Desktop table columns (aligned closer to admin USD ledger):
// Owner | Type | Status | Balance before (net) | Balance after (net) | Reference | Amount | Created
const COL_WIDTHS = ['18%', '9%', '11%', '14%', '14%', '16%', '9%', '9%'];
const PAYMENT_CELL_CLASS = 'px-3 py-3 align-top text-center';
const TABLE_HEAD_BASE = 'h-10 px-4 text-xs font-semibold uppercase tracking-wide text-gray-500';
const TABLE_HEAD_LEFT = cn(TABLE_HEAD_BASE, 'text-left');
const TABLE_HEAD_RIGHT = cn(TABLE_HEAD_BASE, 'text-right');
function FraudWarningIcon({ row, onOpenFraudIndicator, }) {
    var _a;
    const fraudId = (_a = row.fraudFlag) === null || _a === void 0 ? void 0 : _a.id;
    const clickable = Boolean(fraudId && onOpenFraudIndicator);
    const icon = (React.createElement(AlertTriangle, { className: cn('h-3.5 w-3.5 shrink-0 text-amber-600', clickable && 'cursor-pointer hover:text-amber-700'), "aria-hidden": true }));
    if (clickable) {
        return (React.createElement("button", { type: "button", onClick: (e) => {
                e.stopPropagation();
                onOpenFraudIndicator(fraudId);
            }, className: "inline-flex shrink-0 rounded p-0.5 hover:bg-amber-50", "aria-label": "Flagged by fraud indicator", title: "Flagged by fraud indicator" }, icon));
    }
    return (React.createElement("span", { title: "Flagged by fraud indicator", "aria-label": "Flagged by fraud indicator" }, icon));
}
function truncateMonoId(txId, fallbackId) {
    const full = txId || fallbackId || '—';
    const display = full.length > 12 && txId ? `${txId.slice(0, 8)}…` : full;
    return { display, full };
}
function TransactionBlock({ row, onOpenFraudIndicator, }) {
    var _a, _b, _c, _d;
    const txId = String((_b = (_a = row.transactionId) !== null && _a !== void 0 ? _a : row.transaction_id) !== null && _b !== void 0 ? _b : '').trim();
    const ledgerId = String((_c = row.id) !== null && _c !== void 0 ? _c : '').trim();
    const { display, full } = truncateMonoId(txId, ledgerId);
    const copyText = txId || ledgerId;
    const paymentLabel = usdLedgerPaymentMethodLabel(row);
    const sourceLabel = usdLedgerSourceLabel(row);
    const fraudFlagged = Boolean((_d = row.fraudFlagged) !== null && _d !== void 0 ? _d : row.fraudFlag);
    return (React.createElement("div", { className: "min-w-0" },
        React.createElement("div", { className: "flex min-w-0 items-center gap-1" },
            fraudFlagged ? React.createElement(FraudWarningIcon, { row: row, onOpenFraudIndicator: onOpenFraudIndicator }) : null,
            React.createElement("span", { className: "min-w-0 truncate font-mono text-xs font-medium text-gray-800", title: full !== '—' ? full : undefined }, display),
            copyText ? React.createElement(CopyTextButton, { text: copyText, label: "Copy transaction ID" }) : null),
        React.createElement("p", { className: "mt-1 truncate text-xs text-gray-600", title: paymentLabel !== '—' ? paymentLabel : undefined }, paymentLabel),
        sourceLabel ? (React.createElement("p", { className: "mt-0.5 truncate text-[11px] text-gray-500", title: sourceLabel }, sourceLabel)) : null));
}
function TransactionIdColumnBlock({ row, onOpenFraudIndicator, }) {
    var _a, _b, _c, _d;
    const txId = String((_b = (_a = row.transactionId) !== null && _a !== void 0 ? _a : row.transaction_id) !== null && _b !== void 0 ? _b : '').trim();
    const ledgerId = String((_c = row.id) !== null && _c !== void 0 ? _c : '').trim();
    const { display, full } = truncateMonoId(txId, ledgerId);
    const copyText = txId || ledgerId;
    const fraudFlagged = Boolean((_d = row.fraudFlagged) !== null && _d !== void 0 ? _d : row.fraudFlag);
    return (React.createElement("div", { className: "min-w-0" },
        React.createElement("div", { className: "flex min-w-0 items-center gap-1" },
            fraudFlagged ? React.createElement(FraudWarningIcon, { row: row, onOpenFraudIndicator: onOpenFraudIndicator }) : null,
            React.createElement("span", { className: "min-w-0 truncate font-mono text-xs font-medium text-gray-800", title: full !== '—' ? full : undefined }, display),
            copyText ? React.createElement(CopyTextButton, { text: copyText, label: "Copy transaction ID" }) : null)));
}
function PaymentColumnBlock({ row }) {
    const paymentLabel = usdLedgerPaymentMethodLabel(row);
    return (React.createElement("p", { className: "break-words text-sm leading-snug text-gray-700" }, paymentLabel));
}
function PartiesBlock({ row }) {
    const from = usdPartyDisplayLabel(row.fromParty, row, 'from');
    const to = usdPartyDisplayLabel(row.toParty, row, 'to');
    return (React.createElement("div", { className: "min-w-0 space-y-1 text-sm leading-snug" },
        React.createElement("p", { className: "truncate whitespace-nowrap text-gray-900", title: from }, from),
        React.createElement("p", { className: "truncate whitespace-nowrap text-gray-900", title: to }, to)));
}
function OwnerColumnBlock({ row }) {
    var _a, _b, _c, _d;
    const type = String((_a = row.type) !== null && _a !== void 0 ? _a : '').toLowerCase();
    const ownerParty = type === 'withdrawal'
        ? (_b = row.fromParty) !== null && _b !== void 0 ? _b : row.toParty
        : type === 'deposit'
            ? (_c = row.toParty) !== null && _c !== void 0 ? _c : row.fromParty
            : (_d = row.toParty) !== null && _d !== void 0 ? _d : row.fromParty;
    const owner = usdPartyDisplayLabel(ownerParty, row, type === 'withdrawal' ? 'from' : 'to');
    return (React.createElement("p", { className: "truncate whitespace-nowrap text-sm leading-snug text-gray-900", title: owner }, owner));
}
function PartyColumnBlock({ label, value, }) {
    return (React.createElement("div", { className: "min-w-0" },
        React.createElement("p", { className: "truncate whitespace-nowrap text-sm leading-snug text-gray-900", title: `${label}: ${value}` }, value)));
}
function FromPartyBlock({ row }) {
    return React.createElement(PartyColumnBlock, { label: "From", value: usdPartyDisplayLabel(row.fromParty, row, 'from') });
}
function ToPartyBlock({ row }) {
    return React.createElement(PartyColumnBlock, { label: "To", value: usdPartyDisplayLabel(row.toParty, row, 'to') });
}
function StatusBlock({ row }) {
    const typeLabel = usdLedgerTypeLabel(row);
    const isDeposit = typeLabel === 'Deposit';
    const isWithdraw = typeLabel === 'Withdraw';
    const st = ledgerDisplayStatus(row);
    const statusLabel = usdLedgerStatusLabel(st);
    return (React.createElement("div", { className: "flex min-w-0 flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:items-center" },
        React.createElement("span", { className: cn('inline-flex w-fit shrink-0 rounded-md px-2 py-0.5 text-xs font-semibold', isDeposit && 'bg-emerald-100 text-emerald-800', isWithdraw && 'bg-red-100 text-red-800', !isDeposit && !isWithdraw && 'bg-gray-100 text-gray-700') }, typeLabel),
        React.createElement("span", { className: cn('inline-flex w-fit shrink-0 rounded-md px-2 py-0.5 text-xs font-semibold capitalize', usdLedgerStatusBadgeClass(st)) }, statusLabel)));
}
function TypeBadgeColumnBlock({ row }) {
    const typeLabel = usdLedgerTypeLabel(row);
    const isDeposit = typeLabel === 'Deposit';
    const isWithdraw = typeLabel === 'Withdraw';
    return (React.createElement("span", { className: cn('inline-flex w-fit rounded-md px-2 py-0.5 text-xs font-semibold', isDeposit && 'bg-emerald-100 text-emerald-800', isWithdraw && 'bg-red-100 text-red-800', !isDeposit && !isWithdraw && 'bg-gray-100 text-gray-700') }, typeLabel));
}
function StatusBadgeColumnBlock({ row }) {
    const st = ledgerDisplayStatus(row);
    const statusLabel = usdLedgerStatusLabel(st);
    return (React.createElement("span", { className: cn('inline-flex w-fit rounded-md px-2 py-0.5 text-xs font-semibold capitalize', usdLedgerStatusBadgeClass(st)) }, statusLabel));
}
function AmountBlock({ row }) {
    const signed = signedNetAmount(row);
    return (React.createElement("span", { className: cn('block text-right font-semibold tabular-nums', signed > 1e-9 ? 'text-emerald-700' : signed < -1e-9 ? 'text-red-700' : 'text-gray-700') }, formatUsdAmount(signed)));
}
function BalanceBeforeBlock({ row }) {
    var _a, _b;
    const before = Number((_b = (_a = row.balanceNetBefore) !== null && _a !== void 0 ? _a : row.balance_net_before) !== null && _b !== void 0 ? _b : 0);
    return React.createElement("span", { className: "block text-right tabular-nums text-gray-800" }, formatUsdCurrency(before));
}
function BalanceBlock({ row }) {
    return (React.createElement("span", { className: "block text-right tabular-nums text-gray-800" }, formatUsdCurrency(balanceAfterNet(row))));
}
function ReferenceBlock({ row }) {
    var _a, _b, _c, _d;
    const refType = String((_b = (_a = row.referenceType) !== null && _a !== void 0 ? _a : row.reference_type) !== null && _b !== void 0 ? _b : '').trim();
    const refId = String((_d = (_c = row.referenceId) !== null && _c !== void 0 ? _c : row.reference_id) !== null && _d !== void 0 ? _d : '').trim();
    const text = [refType || null, refId || null].filter(Boolean).join(' · ') || '—';
    return (React.createElement("span", { className: "block truncate font-mono text-xs text-gray-600", title: text }, text));
}
function CreatedBlock({ row }) {
    const created = usdLedgerCreatedAtCompact(row);
    if (!created)
        return React.createElement("span", { className: "block text-right text-gray-500" }, "\u2014");
    return (React.createElement("div", { className: "whitespace-nowrap text-right text-xs leading-snug text-gray-600" },
        React.createElement("div", null, created.dateLine),
        React.createElement("div", { className: "text-gray-500" }, created.timeLine)));
}
function MobileUsdLedgerCard({ row, onViewDetail, onOpenFraudIndicator }) {
    const openDetail = onViewDetail ? () => onViewDetail(row) : undefined;
    return (React.createElement("article", { className: cn('space-y-3 border-b border-gray-100 px-4 py-4 last:border-b-0', openDetail && 'cursor-pointer hover:bg-gray-50/80'), onClick: openDetail, onKeyDown: openDetail
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openDetail();
                }
            }
            : undefined, role: openDetail ? 'button' : undefined, tabIndex: openDetail ? 0 : undefined },
        React.createElement("div", { className: "flex items-start justify-between gap-3" },
            React.createElement("div", { className: "min-w-0 flex-1" },
                React.createElement(TransactionBlock, { row: row, onOpenFraudIndicator: onOpenFraudIndicator }))),
        React.createElement(PartiesBlock, { row: row }),
        React.createElement("div", { className: "flex flex-wrap items-center justify-between gap-3" },
            React.createElement(StatusBlock, { row: row }),
            React.createElement("div", { className: "text-right" },
                React.createElement("p", { className: "text-[11px] font-medium uppercase tracking-wide text-gray-500" }, "Amount"),
                React.createElement(AmountBlock, { row: row }))),
        React.createElement("div", { className: "flex items-end justify-between gap-4" },
            React.createElement("div", null,
                React.createElement("p", { className: "text-[11px] font-medium uppercase tracking-wide text-gray-500" }, "Balance before"),
                React.createElement(BalanceBeforeBlock, { row: row })),
            React.createElement("div", null,
                React.createElement("p", { className: "text-[11px] font-medium uppercase tracking-wide text-gray-500" }, "Balance after"),
                React.createElement(BalanceBlock, { row: row })),
            React.createElement("div", { className: "text-right" },
                React.createElement("p", { className: "text-[11px] font-medium uppercase tracking-wide text-gray-500" }, "Created"),
                React.createElement(CreatedBlock, { row: row })))));
}
export const PortalUsdWalletLedgerTable = ({ rows, loading, isGloballyEmpty, className, onViewDetail, onOpenFraudIndicator, }) => {
    if (loading) {
        return (React.createElement("div", { className: cn('px-4 py-12 text-center text-sm text-gray-500', className) }, "Loading USD transactions\u2026"));
    }
    if (rows.length === 0) {
        return (React.createElement("div", { className: cn('px-4 py-14 text-center', className) },
            React.createElement("p", { className: "text-sm font-medium text-gray-900" }, "No transactions found"),
            React.createElement("p", { className: "mt-1 text-sm text-gray-500" }, isGloballyEmpty
                ? 'Your ledger will appear after deposits, withdrawals, or adjustments.'
                : 'Try changing your filters or search keyword.')));
    }
    return (React.createElement(DataTableShell, { borderless: true, className: className },
        React.createElement("div", { className: "md:hidden" }, rows.map((r) => (React.createElement(MobileUsdLedgerCard, { key: String(r.id), row: r, onViewDetail: onViewDetail, onOpenFraudIndicator: onOpenFraudIndicator })))),
        React.createElement("div", { className: "hidden md:block" },
            React.createElement(Table, { className: "table-fixed w-full" },
                React.createElement("colgroup", null, COL_WIDTHS.map((w) => (React.createElement("col", { key: w, style: { width: w } })))),
                React.createElement(TableHeader, null,
                    React.createElement(TableRow, { className: "border-b border-gray-100 bg-gray-50/90 hover:bg-gray-50/90" },
                        React.createElement(TableHead, { className: TABLE_HEAD_LEFT }, "Owner"),
                        React.createElement(TableHead, { className: TABLE_HEAD_LEFT }, "Type"),
                        React.createElement(TableHead, { className: TABLE_HEAD_LEFT }, "Status"),
                        React.createElement(TableHead, { className: TABLE_HEAD_RIGHT }, "Balance before"),
                        React.createElement(TableHead, { className: TABLE_HEAD_RIGHT }, "Balance after"),
                        React.createElement(TableHead, { className: TABLE_HEAD_LEFT }, "Reference"),
                        React.createElement(TableHead, { className: TABLE_HEAD_RIGHT }, "Amount"),
                        React.createElement(TableHead, { className: TABLE_HEAD_RIGHT }, "Created"))),
                React.createElement(TableBody, null, rows.map((r) => {
                    const openDetail = onViewDetail ? () => onViewDetail(r) : undefined;
                    return (React.createElement(TableRow, { key: String(r.id), className: cn('border-gray-100', openDetail && 'cursor-pointer hover:bg-gray-50/80'), onClick: openDetail, onKeyDown: openDetail
                            ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    openDetail();
                                }
                            }
                            : undefined, tabIndex: openDetail ? 0 : undefined, role: openDetail ? 'button' : undefined },
                        React.createElement(TableCell, { className: "px-4 py-3 align-top" },
                            React.createElement(OwnerColumnBlock, { row: r })),
                        React.createElement(TableCell, { className: "px-4 py-3 align-top" },
                            React.createElement(TypeBadgeColumnBlock, { row: r })),
                        React.createElement(TableCell, { className: "px-4 py-3 align-top" },
                            React.createElement(StatusBadgeColumnBlock, { row: r })),
                        React.createElement(TableCell, { className: "px-4 py-3 align-top" },
                            React.createElement(BalanceBeforeBlock, { row: r })),
                        React.createElement(TableCell, { className: "px-4 py-3 align-top" },
                            React.createElement(BalanceBlock, { row: r })),
                        React.createElement(TableCell, { className: "px-4 py-3 align-top" },
                            React.createElement(ReferenceBlock, { row: r })),
                        React.createElement(TableCell, { className: "px-4 py-3 align-top" },
                            React.createElement(AmountBlock, { row: r })),
                        React.createElement(TableCell, { className: "px-4 py-3 align-top" },
                            React.createElement(CreatedBlock, { row: r }))));
                }))))));
};
export default PortalUsdWalletLedgerTable;
